import React, { useEffect, useMemo, useState } from "react";
import type { AxiosError } from "axios";
import { toast } from "../../../utils/toast";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import {
  OpportunityCardTemplate,
  MarketplaceCardResponseDto,
  UpdateOpportunityCardDto,
} from "../../../types/types";
import {
  createOpportunityCards,
  updateOpportunityCard,
} from "../../../services/api";
import {
  PreviewValues,
  setByPath,
  getByPath,
  isGroupLike,
  normalizeOptions,
  evalVisible,
  slugify,
  buildEmptyRow,
} from "./opportunityFormCommon";
import type { TemplateFieldLike, TemplateStepLike } from "./opportunityFormTypes";
import OpportunityForm, {
  type EventOpportunityDefaults,
  type PanelProps,
} from "./opportunityForm";
import {
  applyOutcomeRegistryDefaults,
  buildOutcomeRegistryFormData,
} from "../../../utils/opportunities/outcomeRegistryHelpers";

interface TemplatePreviewPanelProps {
  template?: OpportunityCardTemplate | null;
  templates?: OpportunityCardTemplate[];
  onCreated?: () => void;
  matchupId?: string | number | null;
  eventDefaults?: EventOpportunityDefaults;
  defaultVisibility?: OpportunityVisibility;
  isModalOpen?: boolean;
  setModalOpen?: (isOpen: boolean) => void;
  selectedCard?: MarketplaceCardResponseDto | null;
  setSelectedCard?: (card: MarketplaceCardResponseDto | null) => void;
  managerId?: number | null;
  fetchData?: () => void;
}

type OpportunityVisibility = "DIRECT_TO_FIGHTER" | "MARKETPLACE";

const eventOpportunityVisibilityOptions: {
  label: string;
  value: OpportunityVisibility;
}[] = [
  { label: "Private", value: "DIRECT_TO_FIGHTER" },
  { label: "Public", value: "MARKETPLACE" },
];

const formatTemplateName = (name: string) =>
  name.replace(/_/g, " ").toLowerCase().replace(/^\w/, (letter) => letter.toUpperCase());

const getTemplateMatchValues = (templateSource: unknown): string[] => {
  const source = templateSource as Record<string, unknown> | null | undefined;
  const templateJson = (source?.templateJson ?? source) as Record<string, unknown> | null | undefined;
  const values = [
    source?.id,
    source?.templateName,
    templateJson?.templateId,
    templateJson?.dealType,
    templateJson?.templateName,
    templateJson?.name,
  ];

  return values
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => String(value).trim().replace(/\s+/g, "_").toLowerCase());
};

const isSameTemplate = (
  templateItem: OpportunityCardTemplate,
  savedTemplateJson: unknown,
): boolean => {
  const templateValues = getTemplateMatchValues(templateItem);
  const savedValues = getTemplateMatchValues(savedTemplateJson);

  return savedValues.some((value) => templateValues.includes(value));
};

const normalizeTemplateSteps = (steps: unknown[]): TemplateStepLike[] => {
  return (Array.isArray(steps) ? steps : []).map((step, index) => {
    const stepRecord = step as Record<string, unknown> | null | undefined;
    const title = String(
      stepRecord?.title || stepRecord?.name || `Step ${index + 1}`,
    );
    const fallbackId =
      stepRecord?.stepId ||
      stepRecord?.id ||
      slugify(title) ||
      `step_${index + 1}`;
    const rawFields = stepRecord?.fields;

    return {
      ...(stepRecord ?? {}),
      stepId: String(fallbackId),
      title,
      fields: Array.isArray(rawFields) ? (rawFields as TemplateFieldLike[]) : [],
    } as TemplateStepLike;
  });
};

const applyEventDefaultsToFields = (
  targetValues: PreviewValues,
  stepId: string,
  fields: TemplateFieldLike[],
  eventDefaults: EventOpportunityDefaults,
  parentPath?: string,
) => {
  fields.forEach((field) => {
    const fieldPath = parentPath
      ? `${parentPath}.${field.key}`
      : `${stepId}.${field.key}`;
    const fieldKey = String(field.key || "").toLowerCase();

    if (fieldKey === "country") {
      setByPath(targetValues, fieldPath, eventDefaults.country ?? "");
    }

    if (fieldKey === "city") {
      setByPath(targetValues, fieldPath, eventDefaults.city ?? "");
    }

    if (fieldKey === "venue") {
      setByPath(targetValues, fieldPath, eventDefaults.venue ?? "");
    }

    if (fieldKey === "sport" || fieldKey === "combatsport" || fieldKey === "combat_sport") {
      setByPath(targetValues, fieldPath, eventDefaults.sport ?? "");
    }

    if (fieldKey === "weightclass" || fieldKey === "weight_class") {
      setByPath(targetValues, fieldPath, eventDefaults.weightClass ?? "");
    }

    if (fieldKey === "eventdate" || fieldKey === "event_date") {
      setByPath(targetValues, fieldPath, eventDefaults.eventDate ?? "");
    }

    if (isGroupLike(field) || field.type === "group") {
      applyEventDefaultsToFields(
        targetValues,
        stepId,
        field.fields || [],
        eventDefaults,
        fieldPath,
      );
    }
  });
};

const applyEventDefaultsToPreviewValues = (
  initialValues: PreviewValues,
  steps: TemplateStepLike[],
  eventDefaults?: EventOpportunityDefaults,
): PreviewValues => {
  if (!eventDefaults) return initialValues;

  const nextValues = JSON.parse(JSON.stringify(initialValues || {})) as PreviewValues;

  steps.forEach((step) => {
    if (step.repeatable) return;

    applyEventDefaultsToFields(
      nextValues,
      step.stepId,
      step.fields || [],
      eventDefaults,
    );
  });

  return nextValues;
};

const OpportunityFormContainer: React.FC<TemplatePreviewPanelProps> = ({
  template,
  templates = [],
  onCreated,
  matchupId,
  eventDefaults,
  defaultVisibility = "MARKETPLACE",
  isModalOpen,
  setModalOpen,
  selectedCard,
  setSelectedCard,
  managerId,
  fetchData,
}) => {
  const isEditMode = !!(isModalOpen !== undefined && setModalOpen);

  const { t: translateText, i18n } = useTranslation("CreateEditOpportunities");

  const showVisibilityTabs = !isEditMode && Boolean(matchupId);

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem("i18nextLng") || "en");
  }, [i18n]);

  const savedTemplateJson = selectedCard?.opportunityCardResponse?.templateJson;

  const localizedTemplate = !isEditMode || !savedTemplateJson
    ? null
    : templates.find((templateItem) => isSameTemplate(templateItem, savedTemplateJson)) ?? null;

  // Read-only view of the active template JSON for the current mode.
  const activeTemplateJson = isEditMode
    ? localizedTemplate?.templateJson || savedTemplateJson
    : template?.templateJson;

  // Normalized steps derived from the active template JSON.
  const rawSteps = useMemo(
    () => normalizeTemplateSteps(activeTemplateJson?.steps || []),
    [activeTemplateJson],
  );

  const [previewValues, setPreviewValues] = useState<PreviewValues>({});

  const [loading, setLoading] = useState(false);

  const [visibility, setVisibility] = useState<OpportunityVisibility>(
    "DIRECT_TO_FIGHTER",
  );

  // Steps filtered to those currently visible (visible / visibleWhen).
  const steps = rawSteps.filter((step) => {
    if (step?.visible === false) return false;
    if (step?.visibleWhen) return evalVisible(step.visibleWhen, previewValues);
    return true;
  });

  // Seed previewValues with saved data (edit mode) or registry defaults (create mode).
  useEffect(() => {
    if (isEditMode) {
      if (selectedCard && isModalOpen) {
        const savedOpportunity = selectedCard.opportunityCardResponse;
        const existingValues = savedOpportunity?.formData
          ? JSON.parse(JSON.stringify(savedOpportunity.formData))
          : {};
        setPreviewValues(
          applyOutcomeRegistryDefaults(existingValues, rawSteps),
        );
      } else if (!isModalOpen) {
        setPreviewValues({});
      }
    } else {
      const initialValues: PreviewValues = {};
      (rawSteps || []).forEach((step) => {
        if (step.outcomeRegistry) {
          initialValues[step.stepId] = buildOutcomeRegistryFormData(
            step.outcomeRegistry,
          );
        } else if (step.repeatable) {
          // Same default as the backend: a repeatable step needs at least one row unless minItems says otherwise.
          const initialRowCount = step.minItems ?? 1;
          const initialRows: Record<string, unknown>[] = [];
          for (let rowNumber = 0; rowNumber < initialRowCount; rowNumber++) {
            initialRows.push(buildEmptyRow(step.fields));
          }
          initialValues[step.stepId] = initialRows;
        } else {
          initialValues[step.stepId] = {};
          (step.fields || []).forEach((field) => {
            const path = `${step.stepId}.${field.key}`;
            setByPath(initialValues, path, field.default !== undefined ? field.default : null);
            if (isGroupLike(field)) {
              const nestedValues: Record<string, unknown> = {};
              (field.fields || []).forEach((nestedField) => (nestedValues[nestedField.key] = nestedField.default ?? null));
              setByPath(initialValues, path, nestedValues);
            }
          });
        }
      });
      setPreviewValues(
        applyEventDefaultsToPreviewValues(initialValues, rawSteps, eventDefaults),
      );
    }
  }, [
    isEditMode,
    isModalOpen,
    selectedCard,
    template,
    rawSteps,
    eventDefaults,
  ]);

  // Builds dropdown options from another step's field values ("stepId.fieldKey").
  const getOptionsFromAnotherStep = (
    optionsFrom: string,
  ): { label: string; value: string }[] => {
    if (!optionsFrom) return [];
    const dotIndex = optionsFrom.indexOf(".");
    if (dotIndex === -1) return [];
    const stepId = optionsFrom.slice(0, dotIndex);
    const fieldKey = optionsFrom.slice(dotIndex + 1);
    const sourceRows = getByPath(previewValues, stepId);
    if (!Array.isArray(sourceRows)) return [];
    const sourceStep = steps.find((step) => step.stepId === stepId);
    const sourceField = sourceStep?.fields?.find((field) => field.key === fieldKey);
    const normalizedSourceOptions = normalizeOptions(sourceField?.options ?? []);
    const seenValues = new Set<string>();
    const options: { label: string; value: string }[] = [];
    sourceRows.forEach((row) => {
      const rawValue = getByPath(row, fieldKey);
      if (rawValue === undefined || rawValue === null || rawValue === "") return;
      const valueText = String(rawValue);
      if (!seenValues.has(valueText)) {
        seenValues.add(valueText);
        const matchedOption = normalizedSourceOptions.find((option) => option.value === valueText);
        options.push({
          label:
            matchedOption?.label ||
            valueText
              .replaceAll("_", " ")
              .toLowerCase()
              .replace(/\b\w/g, (letter) => letter.toUpperCase()),
          value: valueText,
        });
      }
    });
    return options;
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const formData = (() => {
        const formDataByStep: Record<string, unknown> = {};
        for (const step of steps) {
          if (step.repeatable) {
            formDataByStep[step.stepId] = getByPath(previewValues, step.stepId) || [];
          } else if (step.outcomeRegistry) {
            formDataByStep[step.stepId] = buildOutcomeRegistryFormData(
              step.outcomeRegistry,
              getByPath(previewValues, step.stepId),
              previewValues,
            );
          } else {
            const stepValues: Record<string, unknown> = {};
            for (const field of step.fields || []) {
              const raw = getByPath(previewValues, `${step.stepId}.${field.key}`);

              if (isGroupLike(field) || field.type === "group")
                stepValues[field.key] = raw || {};
              else if (field.type === "multiselect")
                stepValues[field.key] = Array.isArray(raw) ? raw : [];
              else
                stepValues[field.key] = raw ?? null;

              if (field.type === "fighter_search") {
                const nameKey = field.nameField || "opponentFighterName";
                const nameValue = getByPath(previewValues, `${step.stepId}.${nameKey}`);
                stepValues[nameKey] = nameValue ?? null;
              }
            }
            formDataByStep[step.stepId] = stepValues;
          }
        }
        return formDataByStep;
      })();

      const templateId = template?.id ?? template?.templateJson?.templateId;

      if (!templateId) {
        toast.error(translateText("templateIdRequired"));
        return;
      }

      await createOpportunityCards({
        templateId: String(templateId),
        templateJson: template?.templateJson,
        formData,
        matchupId: matchupId ? Number(matchupId) : undefined,
        visibility: showVisibilityTabs ? visibility : defaultVisibility,
      });
      toast.success(translateText("successCreate"));
      onCreated?.();
    } catch (err: unknown) {
      const axiosErr = err as AxiosError<{ message?: string }> | undefined;
      toast.error(
        translateText("createFailed", {
          message: axiosErr?.response?.data?.message || axiosErr?.message || translateText("unknownError"),
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!managerId || !selectedCard) {
      toast.error(translateText("managerIdError") || "Manager ID is required");
      return;
    }

    if (
      selectedCard.opportunityCardResponse?.status?.toUpperCase() !==
      "AVAILABLE"
    ) {
      toast.error(
        translateText("onlyAvailableOpportunityCardsCanBeEdited"),
      );
      return;
    }

    setLoading(true);

    const isUpfront =
      String(
        (previewValues?.core as Record<string, unknown> | undefined)
          ?.paymentMode ?? "",
      )
        .toLowerCase()
        .trim() !== "split";

    const previewValuesWithPolicies = applyOutcomeRegistryDefaults(
      previewValues,
      steps,
    );

    const finalFormData = isUpfront
      ? {
        ...previewValuesWithPolicies,
        components: ((previewValuesWithPolicies.components ?? []) as Array<Record<string, unknown>>).map((component) => {
          const { paymentTiming, payment_timing, paymentTimingKey, ...rest } = component;
          return rest;
        }),
      }
      : previewValuesWithPolicies;

    const payload: UpdateOpportunityCardDto = {
      marketplaceCardId: selectedCard.id,
      templateJson: activeTemplateJson,
      formData: finalFormData,
    };

    try {
      await updateOpportunityCard(payload);
      toast.success(translateText("successUpdate") || "Opportunity updated successfully!");
      fetchData?.();
      setModalOpen?.(false);
      setSelectedCard?.(null);
      setPreviewValues({});
    } catch (err: unknown) {
      const error = err as AxiosError<{
        message: string;
        errors?: { field: string; message: string }[];
      }>;
      let msg = translateText("unexpectedError") || "Something went wrong";
      if (error.response) {
        const responseData = error.response.data as { errors?: { field: string; message: string }[]; message?: string } | undefined;
        if (error.response.status === 400 && responseData?.errors) {
          msg = responseData.errors.map((errorItem) => `${errorItem.field}: ${errorItem.message}`).join("; ");
        } else if (responseData?.message) {
          msg = responseData.message;
        } else if (error.response.status === 500) {
          msg = translateText("serverError") || "Server error. Please try again.";
        } else if (error.response.status === 401) {
          msg = translateText("unauthorizedError") || "Unauthorized. Please log in again.";
        }
      } else if (error.request) {
        msg = translateText("networkError") || "Network error. Check your connection.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setModalOpen?.(false);
    setSelectedCard?.(null);
    setPreviewValues({});
  };

  const rawTemplateName = isEditMode
    ? localizedTemplate?.templateName ||
    activeTemplateJson?.name ||
    activeTemplateJson?.templateName
    : template?.templateName;

  const templateName = rawTemplateName
    ? formatTemplateName(rawTemplateName)
    : undefined;

  const visibilityTabs = showVisibilityTabs ? (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
      {eventOpportunityVisibilityOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => setVisibility(option.value)}
          className={`rounded-md px-4 py-1.5 text-xs font-semibold transition ${
            visibility === option.value
              ? "bg-blue-600 text-white shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  ) : undefined;

  const innerProps: PanelProps = {
    steps,
    allSteps: rawSteps,
    previewValues,
    setPreviewValues,
    loading,
    isEditMode,
    templateName,
    onSubmit: isEditMode ? handleEdit : handleCreate,
    onClose: isEditMode ? handleClose : undefined,
    resolveOptionsFrom: getOptionsFromAnotherStep,
    eventDefaults,
    headerAction: visibilityTabs,
  };

  if (isEditMode) {
    if (!isModalOpen) return null;
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="w-full sm:w-[860px] sm:max-w-[95vw] h-full sm:h-auto sm:max-h-[92vh] bg-white sm:rounded-2xl shadow-2xl sm:border sm:border-gray-200 flex flex-col overflow-hidden">
          <div className="flex items-center gap-3 px-4 sm:px-5 py-4 sm:pt-4 sm:pb-3 border-b border-gray-100 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="sm:hidden size-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 hover:bg-gray-200 transition-colors shrink-0"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-[15px] font-bold text-gray-900 leading-snug truncate">
                {translateText("editOpportunity") || "Edit Opportunity"}
              </h2>
              {templateName && (
                <p className="text-[12px] text-gray-500 mt-0.5 truncate">{templateName}</p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="hidden sm:flex size-7 items-center justify-center rounded-xl bg-gray-100 text-gray-500 outline-none transition-colors hover:bg-gray-200 shrink-0"
            >
              <XMarkIcon className="size-4" strokeWidth={2} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
            <OpportunityForm {...innerProps} />
          </div>
        </div>
      </div>
    );
  }

  return <OpportunityForm {...innerProps} />;
};

export default OpportunityFormContainer;
